// Teams channel lists grouped by team (team picture + name as a heading,
// channel names under it), shared by the Teams admin page and pickers.
import React from 'react'
import { Avatar, Box, ListSubheader, MenuItem, Typography } from '@mui/material'
import { pathPrefix } from '../env'

export type Channel = { value: string; team: string; channel: string }

// teamPhoto is the picture of the Microsoft Teams team a channel belongs to.
export const teamPhoto = (channel: string): string =>
  `${pathPrefix}/api/v2/erecords/team-photo/${encodeURIComponent(channel.split('/')[0])}`

// fromLabel turns a "Team › Channel" label into a Channel.
export function fromLabel(value: string, label: string): Channel {
  const [team, ...rest] = label.split(' › ')
  return { value, team, channel: rest.join(' › ') || label }
}

const byName = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base' })

export function groupByTeam(
  list: Channel[],
): { team: string; teamID: string; channels: Channel[] }[] {
  const m = new Map<
    string,
    { team: string; teamID: string; channels: Channel[] }
  >()
  for (const ch of list) {
    const teamID = ch.value.split('/')[0]
    const g = m.get(teamID) ?? { team: ch.team, teamID, channels: [] }
    g.channels.push(ch)
    m.set(teamID, g)
  }
  const groups = Array.from(m.values()).sort((a, b) => byName(a.team, b.team))
  groups.forEach((g) => g.channels.sort((a, b) => byName(a.channel, b.channel)))
  return groups
}

export function TeamHeading(props: {
  team: string
  channel: string
}): React.JSX.Element {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar
        variant='rounded'
        src={teamPhoto(props.channel)}
        alt=''
        sx={{ width: 22, height: 22 }}
      />
      <Typography component='span' sx={{ fontSize: 13, fontWeight: 600 }}>
        {props.team}
      </Typography>
    </Box>
  )
}

// channelMenuItems renders select options grouped by team.
export function channelMenuItems(list: Channel[]): React.ReactNode[] {
  return groupByTeam(list).flatMap((g) => [
    <ListSubheader key={'h' + g.teamID} sx={{ lineHeight: '36px', pt: 0.5 }}>
      <TeamHeading team={g.team} channel={g.channels[0].value} />
    </ListSubheader>,
    ...g.channels.map((ch) => (
      <MenuItem key={ch.value} value={ch.value} sx={{ pl: 5.5 }}>
        {ch.channel}
      </MenuItem>
    )),
  ])
}

// renderChannel shows a selected channel as team picture + "Team › Channel".
export function renderChannel(list: Channel[]) {
  return function Render(value: unknown): React.ReactNode {
    const ch = list.find((c) => c.value === value)
    if (!ch) return ''
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          variant='rounded'
          src={teamPhoto(ch.value)}
          alt=''
          sx={{ width: 20, height: 20 }}
        />
        {ch.team} › {ch.channel}
      </Box>
    )
  }
}
